import { Link } from "react-router-dom";
import { ArrowRight, Users, Stethoscope, Briefcase, Calculator, HeartHandshake, Baby } from "lucide-react";
import Seo from "../components/site/Seo";
import { Section, Timeline, CtaBand } from "../components/site/Bits";
import { TRIAL_STEPS, MOD_STEPS, TRUST } from "../components/site/data";
import { useSite } from "../lib/site";
import { mediaUrl } from "../lib/api";

const WHO = [
  { icon: Users, title: "Participants", body: "Adults and children with an NDIS plan who want to try equipment before committing." },
  { icon: HeartHandshake, title: "Families and carers", body: "Support for the people who help day to day, including safe transfers and bathing." },
  { icon: Stethoscope, title: "OTs and physios", body: "Home trials with clear documentation to support your AT and home modification recommendations." },
  { icon: Briefcase, title: "Support coordinators", body: "A simple referral process and regular updates, from enquiry to delivery." },
  { icon: Calculator, title: "Plan managers", body: "Clear quotes and invoices that match the approved supports." },
  { icon: Baby, title: "Paediatric clients", body: "Child-sized equipment and family-friendly trials." },
];

function Testimonials({ items }) {
  if (!items?.length) return null;
  return (
    <Section id="testimonials" eyebrow="In their words" title="What people say about us" tone="bg-surface">
      <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="testimonials-list">
        {items.map((t, i) => (
          <li key={i} className="card p-7">
            <blockquote><p className="text-lg">“{t.quote}”</p></blockquote>
            <p className="mt-4 font-semibold">{t.name}{t.context ? <span className="font-normal text-ink-muted"> · {t.context}</span> : null}</p>
          </li>
        ))}
      </ul>
    </Section>
  );
}

export default function Home() {
  const { content, settings } = useSite();
  const h = content.home || {};
  return (
    <>
      <Seo title="NDIS Equipment Trials & Home Modifications Melbourne" path="/"
        description="Try assistive technology at home before you buy. NDIS equipment trials and home modifications across Melbourne and Victoria, with trial reports and quotes to support funding." />
      <section className="relative overflow-hidden bg-surface">
        <div className="container-x grid items-center gap-12 py-14 lg:grid-cols-[1.15fr_1fr] lg:py-24">
          <div className="fade-up">
            <p className="eyebrow">{h.hero_eyebrow || `${settings.service_area} · Assistive technology & home modifications`}</p>
            <h1 className="mt-4 font-display text-4xl font-bold leading-[1.1] text-teal sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {h.hero_title || "Try it at home before you buy"}
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-ink-muted sm:text-xl">{h.hero_subtitle}</p>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/request-trial" className="btn-primary text-lg" data-testid="hero-request-trial-btn">Request a Trial</Link>
              <Link to="/referrers#referral-form" className="btn-accent text-lg" data-testid="hero-referral-btn">Make a Referral</Link>
            </div>
            <p className="mt-6 text-ink-muted">Participants, families and professionals welcome · Servicing {settings.service_area}</p>
          </div>
          <div className="fade-up relative" style={{ animationDelay: "150ms" }}>
            <img src={mediaUrl(h.hero_image) || "/images/hero-kitchen.jpg"} alt="A woman using a wheelchair washes dishes at a kitchen sink in her home" className="aspect-[4/5] w-full rounded-[2.5rem] object-cover sm:aspect-[5/5]" fetchPriority="high" />
            <div className="card absolute -bottom-6 left-4 max-w-xs p-5 shadow-xl sm:left-[-2rem]">
              <p className="font-display text-lg font-semibold text-teal">Your home, your routine</p>
              <p className="mt-1 text-[0.95rem] text-ink-muted">Trial equipment where you'll really use it, with your therapist involved.</p>
            </div>
          </div>
        </div>
      </section>

      <Section id="paths" eyebrow="Two ways we help" title="Choose where you'd like to start">
        <div className="grid gap-6 lg:grid-cols-2">
          {[
            { to: "/equipment-trials", title: "Equipment trials", img: "/images/walker.jpg", alt: "An older woman walking with a rollator outside large windows", body: h.trials_blurb, cta: "How trials work", id: "trials" },
            { to: "/home-modifications", title: "Home modifications", img: "/images/bathroom-rails.jpg", alt: "A bright accessible bathroom with grab rails beside the toilet", body: h.mods_blurb, cta: "Explore home modifications", id: "mods" },
          ].map((p) => (
            <Link key={p.to} to={p.to} className="card group grid overflow-hidden transition-shadow hover:shadow-xl sm:grid-cols-[1fr_1.1fr]" data-testid={`path-card-${p.id}`}>
              <img src={p.img} alt={p.alt} loading="lazy" className="h-60 w-full object-cover sm:h-full" />
              <div className="flex flex-col p-7">
                <h3 className="font-display text-2xl font-semibold text-teal">{p.title}</h3>
                <p className="mt-3 flex-1 text-ink-muted">{p.body}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-semibold text-teal group-hover:underline">{p.cta}<ArrowRight size={20} aria-hidden="true" className="transition-transform group-hover:translate-x-1" /></span>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      <Section id="how-it-works" eyebrow="How it works" title="From first conversation to equipment you can rely on" tone="bg-surface"
        intro="Our trial-to-purchase process means equipment is tested in your home before any funding is requested.">
        <Timeline steps={TRIAL_STEPS} testid="home-trial-timeline" />
        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/equipment-trials" className="btn-outline">Read the full trial process</Link>
          <Link to="/home-modifications#process" className="btn-outline">See the home modification process ({MOD_STEPS.length} steps)</Link>
        </div>
      </Section>

      <Section id="who-we-help" eyebrow="Who we help" title="Working with participants and the people around them">
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {WHO.map((w) => (
            <li key={w.title} className="card p-6">
              <w.icon className="text-teal" size={30} aria-hidden="true" />
              <h3 className="mt-4 font-display text-xl font-semibold">{w.title}</h3>
              <p className="mt-2 text-ink-muted">{w.body}</p>
            </li>
          ))}
        </ul>
      </Section>

      <Section id="trust" eyebrow="Why choose us" title="Clear, careful and on your side" tone="bg-teal-soft">
        <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST.map((t) => (
            <li key={t.title} className="rounded-2xl bg-surface p-6">
              <t.icon className="text-amber-ink" size={30} aria-hidden="true" />
              <h3 className="mt-4 font-display text-lg font-semibold">{t.title}</h3>
              <p className="mt-2 text-ink-muted">{t.body}</p>
            </li>
          ))}
        </ul>
        {settings.ndis_registered && settings.ndis_registration_number && (
          <p className="mt-8 inline-flex rounded-xl border-2 border-teal bg-surface px-5 py-3 font-semibold text-teal" data-testid="home-ndis-badge">
            NDIS registered provider · Registration {settings.ndis_registration_number}
          </p>
        )}
      </Section>

      <Testimonials items={content.testimonials?.items} />
      <CtaBand />
    </>
  );
}
